import React, { useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronDown } from 'lucide-react';
import { nailConditions, NailCondition } from "./nailConditions";
import { useTranslation } from 'react-i18next';

interface AnalysisDetailsDialogProps {
  eyesAnalysis: any;
  nailsAnalysis: any;
}

const AnalysisDetailsDialog: React.FC<AnalysisDetailsDialogProps> = ({
  eyesAnalysis,
  nailsAnalysis
}) => {
  const { t } = useTranslation();

  console.log('nailsAnalysis', nailsAnalysis);

  const drawCanvas = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx || !eyesAnalysis?.image) return;
  
    const img = new Image();
    img.onload = () => {
      const originalWidth = eyesAnalysis.data.image.width;
      const originalHeight = eyesAnalysis.data.image.height;
      
      const maxWidth = canvas.clientWidth;
      const maxHeight = 400;
      
      let canvasWidth = maxWidth;
      let canvasHeight = (originalHeight / originalWidth) * maxWidth;
      
      if (canvasHeight > maxHeight) {
        canvasHeight = maxHeight;
        canvasWidth = (originalWidth / originalHeight) * maxHeight;
      }
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
  
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
  
      const scaleX = canvasWidth / originalWidth;
      const scaleY = canvasHeight / originalHeight;
  
      eyesAnalysis.data.predictions?.forEach((pred: any) => {
        // Convert center coordinates to top-left coordinates
        const x = (pred.x - pred.width / 2) * scaleX;
        const y = (pred.y - pred.height / 2) * scaleY;
        const width = pred.width * scaleX;
        const height = pred.height * scaleY;
  
        // Draw box
        ctx.strokeStyle = '#dc2626';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, width, height);
  
        // Draw label background for better readability
        const label = `${pred.class || 'Desconocido'}: ${Math.round(pred.confidence * 100)}%`;
        ctx.font = '12px sans-serif';
        const labelWidth = ctx.measureText(label).width + 4;
        
        ctx.fillStyle = '#dc2626';
        ctx.fillRect(x, y - 20, labelWidth, 16);
        
        // Draw label text
        ctx.fillStyle = '#ffffff';
        ctx.fillText(label, x + 2, y - 8);
      });
    };
    img.src = eyesAnalysis.image;
  };
  const drawNailsCanvas = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx || !nailsAnalysis?.image) return;
  
    const img = new Image();
    img.onload = () => {
      const originalWidth = nailsAnalysis.data.image.width;
      const originalHeight = nailsAnalysis.data.image.height;
      
      const maxWidth = canvas.clientWidth;
      const maxHeight = 400;
      
      let canvasWidth = maxWidth;
      let canvasHeight = (originalHeight / originalWidth) * maxWidth;
      
      if (canvasHeight > maxHeight) {
        canvasHeight = maxHeight;
        canvasWidth = (originalWidth / originalHeight) * maxHeight;
      }
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
  
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
  
      const scaleX = canvasWidth / originalWidth;
      const scaleY = canvasHeight / originalHeight;
  
      nailsAnalysis.data.predictions?.forEach((pred: any) => {
        const x = (pred.x - pred.width / 2) * scaleX;
        const y = (pred.y - pred.height / 2) * scaleY;
        const width = pred.width * scaleX;
        const height = pred.height * scaleY;
  
        // Draw box
        ctx.strokeStyle = '#dc2626';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, width, height);
  
        // Draw label background
        const label = `${pred.class || 'Desconocido'}: ${Math.round(pred.confidence * 100)}%`;
        ctx.font = '12px sans-serif';
        const labelWidth = ctx.measureText(label).width + 4;
        
        ctx.fillStyle = '#dc2626';
        ctx.fillRect(x, y - 20, labelWidth, 16);
        
        // Draw label text
        ctx.fillStyle = '#ffffff';
        ctx.fillText(label, x + 2, y - 8);
      });
    };
    img.src = nailsAnalysis.image;
  };
  const getConditionInfo = (className: string): NailCondition | null => {
    return nailConditions.find(
      c => c.condicion.toLowerCase() === className.toLowerCase()
    ) || null;
  };
  

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full mb-6">
          {t('photoEvaluation.analysisDetails.viewDetails')} <ChevronDown className="w-4 h-4 ml-1" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-full h-[100vh] max-h-[100vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('photoEvaluation.analysisDetails.title')}</DialogTitle>
          <DialogDescription>
            {t('photoEvaluation.analysisDetails.description')}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="eyes" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="eyes">{t('photoEvaluation.analysisDetails.tabs.eyes')}</TabsTrigger>
            <TabsTrigger value="nails">{t('photoEvaluation.analysisDetails.tabs.nails')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="eyes" className="pt-4">
            <h3 className="text-lg font-medium mb-3">{t('photoEvaluation.analysisDetails.conjunctiva.title')}</h3>
            {eyesAnalysis?.data ? (
              <div className="space-y-4">
                {eyesAnalysis.image && (
                  <div className="relative mb-4 border rounded-lg overflow-hidden">
                    <canvas
                      ref={(canvas) => canvas && drawCanvas(canvas)}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '400px',
                        display: 'block'
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">{t('photoEvaluation.analysisDetails.conjunctiva.detectedPredictions')}</h4>
                  <div className="space-y-2">
                    {eyesAnalysis.data.predictions?.map((pred: any, index: number) => (
                      <div key={index} className="bg-white p-3 rounded border">
                        <div className="flex justify-between mb-1">
                          <span className="font-medium">{pred.class || t('unknown')}</span>
                          <span className="bg-fuerte-100 text-fuerte-800 text-xs px-2 py-1 rounded-full">
                            {Math.round(pred.confidence * 100)}% {t('photoEvaluation.analysisDetails.conjunctiva.confidence')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {t('photoEvaluation.analysisDetails.conjunctiva.location')}: X:{Math.round(pred.x)}, Y:{Math.round(pred.y)}, 
                          W:{Math.round(pred.width)}, H:{Math.round(pred.height)}
                        </div>
                      </div>
                    ))}
                    
                    {!eyesAnalysis.data.predictions?.length && (
                      <p className="text-gray-500">{t('photoEvaluation.analysisDetails.conjunctiva.noElements')}</p>
                    )}
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  <p>{t('photoEvaluation.analysisDetails.processingTime')}: {Number(eyesAnalysis.data.time).toFixed(2)}ms</p>
                  <p>{t('photoEvaluation.analysisDetails.model')}: {eyesAnalysis.data.model || 'konjungtiva-2-gr7ta'}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">{t('photoEvaluation.errors.processEyeImage')}</p>
            )}
          </TabsContent>
          
          <TabsContent value="nails" className="pt-4">
            <h3 className="text-lg font-medium mb-3">{t('photoEvaluation.analysisDetails.nails.title')}</h3>
            {nailsAnalysis?.data ? (
              <div className="space-y-4">
                {nailsAnalysis.image && (
                  <div className="relative mb-4 border rounded-lg overflow-hidden">
                    <canvas
                      ref={(canvas) => canvas && drawNailsCanvas(canvas)}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '400px',
                        display: 'block'
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">{t('photoEvaluation.analysisDetails.nails.detectedPredictions')}</h4>
                  <div className="space-y-2">
                    {nailsAnalysis.data.predictions?.map((pred: any, index: number) => {
                      const conditionInfo = getConditionInfo(pred.class);
                      return (
                        <div key={index} className="bg-white p-3 rounded border">
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">{pred.class || t('unknown')}</span>
                            <span className="bg-sangre-100 text-sangre-800 text-xs px-2 py-1 rounded-full">
                              {Math.round(pred.confidence * 100)}% {t('photoEvaluation.analysisDetails.nails.confidence')}
                            </span>
                          </div>
                          
                          {conditionInfo && (
                            <div className="mt-2 space-y-2">
                              <p className="text-sm text-gray-700">{conditionInfo.descripcion}</p>
                              <div className={`p-2 rounded text-sm ${
                                conditionInfo.relacion_anemia === true 
                                  ? 'bg-red-50 text-red-700'
                                  : conditionInfo.relacion_anemia === 'posible'
                                  ? 'bg-yellow-50 text-yellow-700'
                                  : 'bg-gray-50 text-gray-700'
                              }`}>
                                <p>{conditionInfo.descripcion_relacion_anemia}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {!nailsAnalysis.data.predictions?.length && (
                      <p className="text-gray-500">{t('photoEvaluation.analysisDetails.nails.noElements')}</p>
                    )}
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  <p>{t('photoEvaluation.analysisDetails.processingTime')}: {Number(nailsAnalysis.data.time).toFixed(2)}ms</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">{t('photoEvaluation.errors.processImage')}</p>
            )}
          </TabsContent>
        </Tabs>
        
        <DialogClose asChild>
          <Button variant="outline" className="w-full mt-4">{t('photoEvaluation.analysisDetails.close')}</Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};

export default AnalysisDetailsDialog;
